import { Fragment, type ReactElement } from "react";

import type { IconName } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";

import { useTranslation } from "pro-features/components/WithTranslation";

import type { PluginRenderResult } from "../../plugins/types";
import type { SearchableSelectionOption } from "../../types";
import { ColumnOptionLabel } from "../ColumnOptionLabel/ColumnOptionLabel";

import {
    StyledActionsContainer,
    StyledCloseIconWrapper,
    StyledColumnOption,
    StyledDragIcon,
    StyledFlexRow,
    StyledIcon,
    StyledIconButton,
    StyledIconButtonWrapper, StyledRequiredIconWrapper,
    StyledSelectedColumnOption,
} from "./styles";

type ColumnOptionButtonProps = {
    tooltip: string;
    iconName: IconName;
    onClick: VoidFunction;
};

const ColumnOptionButton = (props: ColumnOptionButtonProps): ReactElement => {
    const { tooltip, onClick, iconName } = props;

    return (
        <Tooltip content={tooltip}>
            <StyledIconButtonWrapper>
                <StyledIconButton
                    iconName={iconName}
                    iconSize="14px"
                    onClick={onClick}
                    data-automation="column-option-add-button"
                />
            </StyledIconButtonWrapper>
        </Tooltip>
    );
};

type ColumnOptionViewProps = SearchableSelectionOption & {
    searchTerm?: string;
    isDraggable?: boolean;
    badgePlugins?: readonly PluginRenderResult[];
    actionPlugins?: readonly PluginRenderResult[];
    onClick: VoidFunction;
};

/**
 * Takes the option as a single prop rather than spread, so the memoised row wrapping this compares
 * one reference instead of every field.
 */
type NotSelectedColumnOptionViewProps = {
    column: SearchableSelectionOption;
    searchTerm?: string;
    badgePlugins?: readonly PluginRenderResult[];
    actionPlugins?: readonly PluginRenderResult[];
    onClick: VoidFunction;
};

export const NotSelectedColumnOptionView = ({
    column,
    searchTerm,
    badgePlugins = [],
    actionPlugins = [],
    onClick,
}: NotSelectedColumnOptionViewProps): ReactElement => {
    const { label, additionalLabel } = column;
    const translate = useTranslation();

    const substitutePlugin = actionPlugins.find((plugin) => plugin.behavior === "substitute");

    const defaultButton = (
        <ColumnOptionButton
            tooltip={translate(
                "si.components.advanced_search_results_table.column_picker.columns_to_add_list.item_tooltip",
            )}
            iconName="plus"
            onClick={onClick}
        />
    );

    return (
        <StyledColumnOption>
            <ColumnOptionLabel
                label={label}
                additionalLabel={additionalLabel}
                searchTerm={searchTerm}
            />
            <StyledActionsContainer>
                {badgePlugins.map((plugin, index) => (
                    <Fragment key={index}>{plugin.component}</Fragment>
                ))}

                {substitutePlugin ? substitutePlugin.component : defaultButton}
            </StyledActionsContainer>
        </StyledColumnOption>
    );
};

export const SelectedColumnOptionView = ({
    label,
    additionalLabel,
    isDraggable,
    isRequired,
    badgePlugins = [],
    onClick,
}: ColumnOptionViewProps): ReactElement => {
    const translate = useTranslation();

    const renderButton = () => {
        if (isRequired) {
            return (
                <StyledRequiredIconWrapper>
                    <StyledIcon
                        iconName="locked"
                        iconColor={colorsPalettes.bluegrey[600]}
                        size="16px"
                    />
                </StyledRequiredIconWrapper>
            );
        }

        return (
            <StyledCloseIconWrapper>
                <ColumnOptionButton
                    tooltip={translate(
                        "si.components.advanced_search_results_table.column_picker.added_columns_list.item_tooltip",
                    )}
                    iconName="plus"
                    onClick={onClick}
                />
            </StyledCloseIconWrapper>
        );
    };

    return (
        <StyledSelectedColumnOption>
            <StyledFlexRow>
                {isDraggable ? (
                    <StyledDragIcon
                        iconName="drag-indicator"
                        size="sm"
                    />
                ) : null}

                <ColumnOptionLabel
                    label={label}
                    additionalLabel={additionalLabel}
                />
            </StyledFlexRow>

            <StyledActionsContainer>
                {badgePlugins.map((plugin, index) => (
                    <Fragment key={index}>{plugin.component}</Fragment>
                ))}

                {renderButton()}
            </StyledActionsContainer>
        </StyledSelectedColumnOption>
    );
};
